/** Collapsible wrapper around ChannelSettingsPanel.
 *
 * The delivery settings (destination, reply-to, attach flags, …) are
 * secondary to the message body, so they collapse into a header section.
 * Collapsed, the summary row shows where the message goes plus every flag
 * that is switched ON — enough to sanity-check a method without expanding.
 */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Chip, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import type { NotificationDeliveryMethod } from '@/api/notifications';
import { DELIVERY_METHODS } from '@/api/notifications';

import {
  AIRBNB_DESTINATION_LABELS,
  AIRBNB_TO_ARBITRARY_THREAD,
  CHAT_DESTINATION_LABELS,
  CHAT_ORIGIN_LABELS,
  CHAT_ORIGIN_USER,
  CHAT_TO_ARBITRARY_SID,
  DEST_ARBITRARY,
  EMAIL_ATTACH_FIELDS,
  EMAIL_DESTINATION_LABELS,
  SMS_DESTINATION_LABELS,
  WHATSAPP_DESTINATION_LABELS,
} from './channelFields';
import { ChannelSettingsPanel } from './ChannelSettingsPanel';

interface SummaryPart {
  text: string;
  /** The destination part — rendered emphasized. */
  primary?: boolean;
}

function str(dm: NotificationDeliveryMethod, field: string): string {
  const v = dm[field];
  return typeof v === 'string' ? v.trim() : '';
}

function num(dm: NotificationDeliveryMethod, field: string): number {
  const v = dm[field];
  return typeof v === 'number' ? v : 0;
}

function on(dm: NotificationDeliveryMethod, field: string): boolean {
  return dm[field] === true;
}

/** Compact "where it goes + which flags are on" summary for a collapsed
 *  delivery-settings section. Pure — exported for tests. */
export function channelSettingsSummary(dm: NotificationDeliveryMethod): SummaryPart[] {
  const parts: SummaryPart[] = [];
  const dest = (labels: Record<number, string>, field: string) =>
    labels[num(dm, field)] ?? `#${num(dm, field)}`;

  switch (dm.method) {
    case DELIVERY_METHODS.EMAIL: {
      const d = num(dm, 'email_destination');
      const to =
        d === DEST_ARBITRARY && str(dm, 'email_address')
          ? str(dm, 'email_address')
          : dest(EMAIL_DESTINATION_LABELS, 'email_destination');
      parts.push({ text: `To: ${to}`, primary: true });
      if (str(dm, 'email_replyto')) parts.push({ text: `Reply-to: ${str(dm, 'email_replyto')}` });
      if (on(dm, 'email_append_conversation_sid')) parts.push({ text: 'Append SID' });
      if (on(dm, 'email_allow_override')) parts.push({ text: 'Event override' });
      const attached = EMAIL_ATTACH_FIELDS.filter((f) => on(dm, f.field)).map((f) => f.label);
      if (attached.length > 0) parts.push({ text: `Attach: ${attached.join(', ')}` });
      break;
    }
    case DELIVERY_METHODS.SMS:
    case DELIVERY_METHODS.WHATSAPP: {
      const labels =
        dm.method === DELIVERY_METHODS.SMS ? SMS_DESTINATION_LABELS : WHATSAPP_DESTINATION_LABELS;
      const field = dm.method === DELIVERY_METHODS.SMS ? 'sms' : 'whatsapp';
      const d = num(dm, `${field}_destination`);
      const to =
        d === DEST_ARBITRARY && str(dm, `${field}_number`)
          ? str(dm, `${field}_number`)
          : dest(labels, `${field}_destination`);
      parts.push({ text: `To: ${to}`, primary: true });
      if (on(dm, `${field}_allow_override`)) parts.push({ text: 'Event override' });
      break;
    }
    case DELIVERY_METHODS.CHAT: {
      const d = num(dm, 'chat_destination');
      const to =
        d === CHAT_TO_ARBITRARY_SID && str(dm, 'chat_conversation_sid')
          ? str(dm, 'chat_conversation_sid')
          : dest(CHAT_DESTINATION_LABELS, 'chat_destination');
      parts.push({ text: `To: ${to}`, primary: true });
      const origin = num(dm, 'chat_origin');
      const from =
        origin === CHAT_ORIGIN_USER && dm.chat_origin_user != null
          ? `user #${String(dm.chat_origin_user)}`
          : (CHAT_ORIGIN_LABELS[origin] ?? `#${origin}`);
      parts.push({ text: `From: ${from}` });
      break;
    }
    case DELIVERY_METHODS.AIRBNB: {
      const d = num(dm, 'airbnb_destination');
      const to =
        d === AIRBNB_TO_ARBITRARY_THREAD && str(dm, 'airbnb_thread_id')
          ? `thread ${str(dm, 'airbnb_thread_id')}`
          : dest(AIRBNB_DESTINATION_LABELS, 'airbnb_destination');
      parts.push({ text: `To: ${to}`, primary: true });
      break;
    }
    case DELIVERY_METHODS.SNS_TOPIC:
      parts.push({ text: `Topic: ${str(dm, 'sns_topic') || 'not set'}`, primary: true });
      break;
    case DELIVERY_METHODS.SENTRY: {
      const key = str(dm, 'sentry_tag_key');
      parts.push({
        text: key ? `Tag: ${key}=${str(dm, 'sentry_tag_value')}` : 'No tag configured',
        primary: true,
      });
      break;
    }
    case DELIVERY_METHODS.HTTP_REQUEST:
      parts.push({
        text: `${str(dm, 'http_method') || 'POST'} ${str(dm, 'http_url') || 'no URL'}`,
        primary: true,
      });
      break;
    default:
      return [];
  }
  if (on(dm, 'prevent_replies')) parts.push({ text: 'Prevent replies' });
  return parts;
}

interface Props {
  dm: NotificationDeliveryMethod;
  tenantId: number | null;
  canManage: boolean;
  onFieldChange: (dmId: number, field: string, value: unknown) => void;
}

/** Mount with `key={dm.id}` so the collapse state resets per method. */
export function DeliverySettingsSection({ dm, tenantId, canManage, onFieldChange }: Props) {
  const [expanded, setExpanded] = useState(false);
  const parts = channelSettingsSummary(dm);
  if (dm.method === DELIVERY_METHODS.PUSH_NOTIFICATION) return null;

  return (
    <Accordion
      variant="outlined"
      disableGutters
      expanded={expanded}
      onChange={(_, open) => setExpanded(open)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ minWidth: 0, pr: 1 }}
        >
          <Typography variant="subtitle2" sx={{ flexShrink: 0 }}>
            Delivery settings
          </Typography>
          {!expanded &&
            parts.map((part) => (
              <Chip
                key={part.text}
                size="small"
                variant="outlined"
                color={part.primary ? 'primary' : 'default'}
                label={part.text}
                sx={{ height: 22, fontSize: 12, maxWidth: 280 }}
              />
            ))}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <ChannelSettingsPanel
          dm={dm}
          tenantId={tenantId}
          canManage={canManage}
          onFieldChange={onFieldChange}
        />
      </AccordionDetails>
    </Accordion>
  );
}
